import * as t from 'io-ts';

import {UnreachableError} from '../../../libs/shared/helpers/src/types/customErrors';
import {makeApiType, makeEnumRuntimeType} from './apiTypes';

/*
 * Runtime type.
 */

export enum ServerAuthenticationStrategiesEnum {
  NONE = 'none',
  BASIC = 'basic',
  BEARER = 'bearer',
  OAUTH2 = 'oauth2',
}
const RServerAuthenticationStrategiesEnum = makeEnumRuntimeType(ServerAuthenticationStrategiesEnum);

const RServerNoneAuthenticationConfig = t.readonly(
  t.type({
    strategy: t.literal(ServerAuthenticationStrategiesEnum.NONE),
  }),
);

const RServerBasicAuthenticationConfig = t.readonly(
  t.type({
    strategy: t.literal(ServerAuthenticationStrategiesEnum.BASIC),
  }),
);

export enum ServerSendAsTargetsEnum {
  HEADER = 'header',
  QUERY_PARAM = 'query_param',
}
const RServerSendAsTargets = makeEnumRuntimeType(ServerSendAsTargetsEnum);

const RServerBearerAuthenticationConfig = t.readonly(
  t.type({
    strategy: t.literal(ServerAuthenticationStrategiesEnum.BEARER),
    sendAs: RServerSendAsTargets,
  }),
);

const RServerOAuth2AuthenticationConfig = t.readonly(
  t.type({
    strategy: t.literal(ServerAuthenticationStrategiesEnum.OAUTH2),
    clientId: t.string,
    clientSecret: t.string,
    scope: t.string,
    tokenUrl: t.string,
    authorizationUrl: t.string,
  }),
);

const RServerAuthenticationConfig = t.union([
  RServerNoneAuthenticationConfig,
  RServerBasicAuthenticationConfig,
  RServerBearerAuthenticationConfig,
  RServerOAuth2AuthenticationConfig,
]);

const RReadonlyServer = t.readonly(
  t.type({
    isWritable: t.literal(false),
    id: t.string,
    origin: t.string,
    installationUrl: t.string,
    authenticationConfig: t.type({
      strategy: RServerAuthenticationStrategiesEnum,
    }),
  }),
);

const RWritableServer = t.readonly(
  t.type({
    isWritable: t.literal(true),
    id: t.Integer,
    url: t.string,
    installationUrl: t.union([t.string, t.undefined]),
    origin: t.string,
    authenticationConfig: RServerAuthenticationConfig,
    createdAt: t.Integer,
  }),
);

const RServer = t.union([RReadonlyServer, RWritableServer]);

const RServersSearch = t.readonly(
  t.type({
    items: t.readonlyArray(RServer),
    total: t.number,
    limit: t.number,
  }),
);

/*
 * Type.
 */

export interface ServerBasicAuthenticationConfig extends t.TypeOf<typeof RServerBasicAuthenticationConfig> {}
export interface ServerBearerAuthenticationConfig
  extends t.TypeOf<typeof RServerBearerAuthenticationConfig> {}
// eslint-disable-next-line @typescript-eslint/naming-convention
export interface ServerOAuth2AuthenticationConfig
  extends t.TypeOf<typeof RServerOAuth2AuthenticationConfig> {}
export type ServerAuthenticationConfig = t.TypeOf<typeof RServerAuthenticationConfig>;

export interface ReadonlyServerModel extends t.TypeOf<typeof RReadonlyServer> {}
export interface WritableServerModel extends t.TypeOf<typeof RWritableServer> {}
export type ServerModel = ReadonlyServerModel | WritableServerModel;

export interface UnsavedServerModel
  extends Omit<WritableServerModel, 'isWritable' | 'id' | 'url' | 'installationUrl' | 'createdAt'> {}

export interface ServersSearchModel extends t.TypeOf<typeof RServersSearch> {}

/*
 * IO methods.
 */

function parseServerAuthenticationConfig(src: any): ServerAuthenticationConfig {
  switch (src.authentication_config?.type) {
    case ServerAuthenticationStrategiesEnum.NONE:
      return {
        strategy: ServerAuthenticationStrategiesEnum.NONE,
      };

    case ServerAuthenticationStrategiesEnum.BASIC:
      return {
        strategy: ServerAuthenticationStrategiesEnum.BASIC,
      };

    case ServerAuthenticationStrategiesEnum.BEARER:
      return {
        strategy: ServerAuthenticationStrategiesEnum.BEARER,
        sendAs: src.authentication_config.send_as,
      };

    case ServerAuthenticationStrategiesEnum.OAUTH2:
      return {
        strategy: ServerAuthenticationStrategiesEnum.OAUTH2,
        clientId: src.authentication_config.client_id,
        clientSecret: src.authentication_config.client_secret,
        scope: src.authentication_config.scope,
        tokenUrl: src.authentication_config.token_url,
        authorizationUrl: src.authentication_config.authorization_url,
      };

    default:
      return {
        strategy: ServerAuthenticationStrategiesEnum.NONE,
      };
  }
}

export function parseServer(src: any): ServerModel {
  // If an ID is present, this is a local application server.
  if (src.id) {
    return {
      isWritable: true,
      id: src.id,
      url: src.url,
      installationUrl: src.installation_url,
      origin: src.origin,
      authenticationConfig: parseServerAuthenticationConfig(src),
      createdAt: src.created_at,
    };
  }

  // Otherwise, this is an installed application server.
  return {
    isWritable: false,
    // Navigation requires an ID.
    id: encodeURIComponent(src.origin),
    origin: src.origin,
    installationUrl: src.installation_url,
    // The authentication strategy is required to enter the correct type of credentials.
    authenticationConfig: {
      strategy: src.authentication_config.type,
    },
  };
}

function parseServersSearch(src: any): ServersSearchModel {
  return {
    items: (src.items ?? []).map(parseServer),
    total: src.total,
    limit: src.limit,
  };
}

function serializeServerAuthenticationConfig(authenticationConfig: ServerAuthenticationConfig): any {
  switch (authenticationConfig.strategy) {
    case ServerAuthenticationStrategiesEnum.NONE:
      return {
        type: ServerAuthenticationStrategiesEnum.NONE,
      };

    case ServerAuthenticationStrategiesEnum.BASIC:
      return {
        type: ServerAuthenticationStrategiesEnum.BASIC,
      };

    case ServerAuthenticationStrategiesEnum.BEARER:
      return {
        type: ServerAuthenticationStrategiesEnum.BEARER,
        send_as: authenticationConfig.sendAs,
      };

    case ServerAuthenticationStrategiesEnum.OAUTH2:
      return {
        type: ServerAuthenticationStrategiesEnum.OAUTH2,
        client_id: authenticationConfig.clientId,
        client_secret: authenticationConfig.clientSecret,
        scope: authenticationConfig.scope,
        token_url: authenticationConfig.tokenUrl,
        authorization_url: authenticationConfig.authorizationUrl,
      };

    default:
      throw new UnreachableError(authenticationConfig);
  }
}

export function serializeServer(src: UnsavedServerModel): any {
  return {
    origin: src.origin,
    authentication_config: serializeServerAuthenticationConfig(src.authenticationConfig),
  };
}

/*
 * API type.
 */

export const ApiServer = makeApiType('Server', RServer, parseServer);
export const ApiServersSearch = makeApiType('serversSearch', RServersSearch, parseServersSearch);
